import React, { useCallback, useEffect, useMemo, useState } from "react";
import moment from "moment";
import { FaSpinner } from "react-icons/fa";

import { getContents } from "../../../../FIREBASECONFIG";

const EXCLUDED_RESERVATION_STATUSES = ["cancelled", "cancelled (hide)", "no-show"];

const getSessionUser = () => {
  try {
    return JSON.parse(sessionStorage.getItem("userDetails") || "{}");
  } catch (error) {
    return {};
  }
};

const getDateRange = (startDate, endDate) => {
  const start = moment(startDate, "YYYY-MM-DD", true);
  const end = moment(endDate, "YYYY-MM-DD", true);

  if (!start.isValid() || !end.isValid() || end.isBefore(start, "day")) {
    return [];
  }

  const days = [];
  const daysCount = end.diff(start, "days") + 1;

  for (let i = 0; i < daysCount; i += 1) {
    days.push(start.clone().add(i, "days").format("YYYY-MM-DD"));
  }

  return days;
};

const getCleanStatus = (value) => String(value || "").trim().toLowerCase();

const shouldCountReservation = (reservation) => {
  const status = getCleanStatus(reservation.status);
  const reservationType = getCleanStatus(reservation.reservationType);

  return (
    !EXCLUDED_RESERVATION_STATUSES.includes(status) &&
    !EXCLUDED_RESERVATION_STATUSES.includes(reservationType)
  );
};

const isReservationActiveOnDay = (reservation, day) => {
  const checkIn = moment(reservation.checkIn, "YYYY-MM-DD", true);
  const checkOut = moment(reservation.checkOut, "YYYY-MM-DD", true);
  const dayMoment = moment(day, "YYYY-MM-DD", true);

  if (!checkIn.isValid() || !checkOut.isValid() || !dayMoment.isValid()) {
    return false;
  }

  return (
    dayMoment.isSameOrAfter(checkIn, "day") &&
    dayMoment.isBefore(checkOut, "day")
  );
};

const makeKey = (...parts) => parts.join("__");

const addToMap = (map, key, quantity) => {
  map.set(key, (map.get(key) || 0) + quantity);
};

const getAvailabilityTone = (available, total) => {
  if (total <= 0) return { background: "#f1f3f5", color: "#6c757d" };
  if (available <= 0) return { background: "#ffe3e3", color: "#c92a2a" };
  if (available <= Math.ceil(total * 0.25)) {
    return { background: "#fff3bf", color: "#9c6500" };
  }

  return { background: "#d3f9d8", color: "#2b8a3e" };
};

const calendarTableStyle = {
  borderCollapse: "separate",
  borderSpacing: 0,
};

const getStickyColumnStyle = (minWidth = 260, isHeader = false) => ({
  position: "sticky",
  left: 0,
  minWidth,
  background: isHeader ? "#f8f9fa" : "#fff",
  zIndex: isHeader ? 5 : 4,
  boxShadow: "8px 0 14px -14px rgba(0,0,0,0.45)",
});

const Dashboard = () => {
  const userDetails = useMemo(getSessionUser, []);
  const scopedHotelId = userDetails.hotelId || "";
  const hasScopedHotel = Boolean(scopedHotelId);

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [selectedHotelId, setSelectedHotelId] = useState("");
  const [startDate, setStartDate] = useState(moment().format("YYYY-MM-DD"));
  const [endDate, setEndDate] = useState(
    moment().add(29, "days").format("YYYY-MM-DD")
  );
  const [availability, setAvailability] = useState({
    hotels: [],
    roomPlans: [],
    roomsCount: 0,
  });

  const days = useMemo(() => getDateRange(startDate, endDate), [startDate, endDate]);

  const selectedHotel = useMemo(
    () => availability.hotels.find((hotel) => hotel.id === selectedHotelId),
    [availability.hotels, selectedHotelId]
  );

  const selectedHotelRoomPlans = useMemo(
    () =>
      availability.roomPlans.filter(
        (roomPlan) => roomPlan.hotelId === selectedHotelId
      ),
    [availability.roomPlans, selectedHotelId]
  );

  const visibleDateLabel = useMemo(() => {
    if (!days.length) return "";
    return `${moment(days[0]).format("DD MMM YYYY")} - ${moment(
      days[days.length - 1]
    ).format("DD MMM YYYY")}`;
  }, [days]);

  const loadAvailability = useCallback(async () => {
    if (!days.length) {
      setAvailability({ hotels: [], roomPlans: [], roomsCount: 0 });
      setError("Please select a valid date range.");
      setLoading(false);
      return;
    }

    setLoading(true);
    setError("");

    try {
      const [hotelsData, roomsData, reservationsData] = await Promise.all([
        getContents("hotels"),
        getContents("hotel_rooms"),
        getContents("reservations"),
      ]);

      let hotels = (hotelsData || []).filter((hotel) => hotel.isApproved === true);

      if (hasScopedHotel) {
        const scopedHotel = (hotelsData || []).find(
          (hotel) => hotel.id === scopedHotelId
        );
        hotels = scopedHotel
          ? [scopedHotel]
          : [{ id: scopedHotelId, name: scopedHotelId }];
      }

      const hotelMap = new Map(hotels.map((hotel) => [hotel.id, hotel]));
      const hotelIds = new Set(hotels.map((hotel) => hotel.id));
      const activeRooms = (roomsData || []).filter(
        (room) => hotelIds.has(room.hotel_id) && room.isApproved === true
      );

      const roomsWithPlans = await Promise.all(
        activeRooms.map(async (room) => {
          const plans = await getContents(`hotel_rooms/${room.id}/plans`);
          return {
            ...room,
            plans: (plans || []).filter((plan) => plan.stopSell !== true),
          };
        })
      );

      const soldByHotelDate = new Map();
      const soldByPlanDate = new Map();
      const activeReservations = (reservationsData || []).filter(
        (reservation) =>
          hotelIds.has(reservation.hotelId) && shouldCountReservation(reservation)
      );

      activeReservations.forEach((reservation) => {
        days.forEach((day) => {
          if (!isReservationActiveOnDay(reservation, day)) return;

          (reservation.roomDetails || []).forEach((roomDetail) => {
            const quantity = Number(roomDetail.quantity || 0);
            if (!quantity) return;

            addToMap(soldByHotelDate, makeKey(reservation.hotelId, day), quantity);
            addToMap(
              soldByPlanDate,
              makeKey(
                reservation.hotelId,
                roomDetail.id,
                roomDetail.planId,
                day
              ),
              quantity
            );
          });
        });
      });

      const roomPlans = roomsWithPlans.flatMap((room) =>
        (room.plans || []).map((plan) => {
          const total = Number(plan.totalPlan || plan.roomsAvailable || 0);
          const planName = plan.name || plan.planName || "Default Plan";

          return {
            hotelId: room.hotel_id,
            hotelName: hotelMap.get(room.hotel_id)?.name || room.hotel_id,
            roomId: room.id,
            roomName: room.name || "Unnamed Room",
            planId: plan.id,
            planName,
            total,
            daily: days.map((day) => {
              const sold = Number(
                soldByPlanDate.get(makeKey(room.hotel_id, room.id, plan.id, day)) ||
                  0
              );
              return {
                date: day,
                total,
                sold,
                available: Math.max(total - sold, 0),
              };
            }),
          };
        })
      );

      const hotelsAvailability = hotels.map((hotel) => {
        const hotelRoomPlans = roomPlans.filter(
          (roomPlan) => roomPlan.hotelId === hotel.id
        );
        const total = hotelRoomPlans.reduce(
          (sum, roomPlan) => sum + Number(roomPlan.total || 0),
          0
        );

        return {
          ...hotel,
          total,
          roomsCount: activeRooms.filter((room) => room.hotel_id === hotel.id).length,
          plansCount: hotelRoomPlans.length,
          daily: days.map((day) => {
            const sold = Number(soldByHotelDate.get(makeKey(hotel.id, day)) || 0);
            return {
              date: day,
              total,
              sold,
              available: Math.max(total - sold, 0),
            };
          }),
        };
      });

      setAvailability({
        hotels: hotelsAvailability,
        roomPlans,
        roomsCount: activeRooms.length,
      });
    } catch (loadError) {
      console.error("Resorts dashboard availability load error:", loadError);
      setError("Failed to load resort availability.");
    } finally {
      setLoading(false);
    }
  }, [days, hasScopedHotel, scopedHotelId]);

  useEffect(() => {
    loadAvailability();
  }, [loadAvailability]);

  useEffect(() => {
    if (
      selectedHotelId &&
      !availability.hotels.some((hotel) => hotel.id === selectedHotelId)
    ) {
      setSelectedHotelId("");
    }
  }, [availability.hotels, selectedHotelId]);

  const handlePrevMonth = () => {
    const nextStartDate = moment(startDate).subtract(1, "month");
    setStartDate(nextStartDate.format("YYYY-MM-DD"));
    setEndDate(nextStartDate.clone().add(29, "days").format("YYYY-MM-DD"));
  };

  const handleNextMonth = () => {
    const nextStartDate = moment(startDate).add(1, "month");
    setStartDate(nextStartDate.format("YYYY-MM-DD"));
    setEndDate(nextStartDate.clone().add(29, "days").format("YYYY-MM-DD"));
  };

  const totalCapacity = availability.hotels.reduce(
    (sum, hotel) => sum + Number(hotel.total || 0),
    0
  );
  const firstVisibleDay = days[0];
  const soldOnFirstVisibleDay = availability.hotels.reduce((sum, hotel) => {
    const dayStats = hotel.daily.find((day) => day.date === firstVisibleDay);
    return sum + Number(dayStats?.sold || 0);
  }, 0);

  const renderAvailabilityCell = (stats) => {
    const tone = getAvailabilityTone(stats.available, stats.total);

    return (
      <div
        className="rounded p-2 text-center"
        style={{
          minWidth: 118,
          background: tone.background,
          color: tone.color,
          lineHeight: 1.35,
        }}
      >
        <div className="fw-bold">Available: {stats.available}</div>
        <small>Total: {stats.total}</small>
        <br />
        <small>Sold: {stats.sold}</small>
      </div>
    );
  };

  return (
    <div className="container-fluid">
      <div className="d-flex flex-wrap justify-content-between align-items-center mb-4 gap-3">
        <div>
          <h3 className="mb-1">Resorts Availability Calendar</h3>
          <div className="text-muted">
            {selectedHotel
              ? `Room-wise availability for ${selectedHotel.name}`
              : hasScopedHotel
              ? "Hotel-wise availability for your assigned resort"
              : "Hotel-wise availability for all resorts"}
          </div>
        </div>

        {selectedHotel && (
          <button
            type="button"
            className="btn btn-outline-secondary"
            onClick={() => setSelectedHotelId("")}
          >
            Back to All Hotels
          </button>
        )}
      </div>

      <div className="row mb-4">
        <div className="col-xl-3 col-sm-6 mb-3">
          <div className="card h-100">
            <div className="card-body">
              <div className="text-muted">Hotels</div>
              <h3 className="mb-0">{availability.hotels.length}</h3>
            </div>
          </div>
        </div>
        <div className="col-xl-3 col-sm-6 mb-3">
          <div className="card h-100">
            <div className="card-body">
              <div className="text-muted">Approved Rooms</div>
              <h3 className="mb-0">{availability.roomsCount}</h3>
            </div>
          </div>
        </div>
        <div className="col-xl-3 col-sm-6 mb-3">
          <div className="card h-100">
            <div className="card-body">
              <div className="text-muted">Plan Capacity</div>
              <h3 className="mb-0">{totalCapacity}</h3>
            </div>
          </div>
        </div>
        <div className="col-xl-3 col-sm-6 mb-3">
          <div className="card h-100">
            <div className="card-body">
              <div className="text-muted">
                Sold on {firstVisibleDay ? moment(firstVisibleDay).format("DD MMM") : "-"}
              </div>
              <h3 className="mb-0">{soldOnFirstVisibleDay}</h3>
            </div>
          </div>
        </div>
      </div>

      <div className="card">
        <div className="card-header d-flex flex-wrap justify-content-between align-items-end gap-3">
          <div>
            <h4 className="card-title mb-1">
              {selectedHotel
                ? selectedHotel.name
                : hasScopedHotel
                ? "Assigned Hotel"
                : "All Hotels"}
            </h4>
            <div className="text-muted">{visibleDateLabel}</div>
          </div>

          <div className="d-flex flex-wrap align-items-end gap-2">
            <div>
              <label className="form-label mb-0">Start Date</label>
              <input
                type="date"
                className="form-control"
                value={startDate}
                onChange={(event) => setStartDate(event.target.value)}
              />
            </div>
            <div>
              <label className="form-label mb-0">End Date</label>
              <input
                type="date"
                className="form-control"
                value={endDate}
                onChange={(event) => setEndDate(event.target.value)}
              />
            </div>
            <button
              type="button"
              className="btn btn-outline-primary"
              onClick={handlePrevMonth}
            >
              &lt; Previous Month
            </button>
            <button
              type="button"
              className="btn btn-outline-primary"
              onClick={handleNextMonth}
            >
              Next Month &gt;
            </button>
          </div>
        </div>

        <div className="card-body position-relative">
          {loading && (
            <div
              className="position-absolute w-100 h-100 d-flex align-items-center justify-content-center"
              style={{
                inset: 0,
                background: "rgba(255,255,255,0.85)",
                zIndex: 10,
              }}
            >
              <div className="text-center">
                <FaSpinner className="spin mb-2" style={{ fontSize: 28 }} />
                <div>Loading availability...</div>
              </div>
            </div>
          )}

          {error && <div className="alert alert-danger">{error}</div>}

          {!loading && !error && !selectedHotel && (
            <>
              <div className="text-muted small mb-2">
                Scroll horizontally to view more dates. Hotel names stay pinned on
                the left.
              </div>
              <div className="table-responsive" style={{ overflowX: "auto" }}>
              <table
                className="table table-bordered align-middle mb-0"
                style={calendarTableStyle}
              >
                <thead>
                  <tr>
                    <th style={getStickyColumnStyle(260, true)}>Hotel</th>
                    {days.map((day) => (
                      <th key={day} className="text-center" style={{ minWidth: 138 }}>
                        {moment(day).format("DD MMM YYYY")}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {availability.hotels.length ? (
                    availability.hotels.map((hotel) => (
                      <tr key={hotel.id}>
                        <td style={getStickyColumnStyle(260)}>
                          <button
                            type="button"
                            className="btn btn-link p-0 text-start fw-bold"
                            onClick={() => setSelectedHotelId(hotel.id)}
                          >
                            {hotel.name || "Unnamed Hotel"}
                          </button>
                          <div className="text-muted small">
                            {hotel.roomsCount} rooms, {hotel.plansCount} plans
                          </div>
                        </td>
                        {hotel.daily.map((stats) => (
                          <td key={stats.date}>{renderAvailabilityCell(stats)}</td>
                        ))}
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td colSpan={days.length + 1} className="text-center py-4">
                        No approved hotels found.
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
              </div>
            </>
          )}

          {!loading && !error && selectedHotel && (
            <>
              <div className="text-muted small mb-2">
                Scroll horizontally to view more dates. Room names stay pinned on
                the left.
              </div>
              <div className="table-responsive" style={{ overflowX: "auto" }}>
              <table
                className="table table-bordered align-middle mb-0"
                style={calendarTableStyle}
              >
                <thead>
                  <tr>
                    <th style={getStickyColumnStyle(280, true)}>
                      Room / Rate Plan
                    </th>
                    {days.map((day) => (
                      <th key={day} className="text-center" style={{ minWidth: 138 }}>
                        {moment(day).format("DD MMM YYYY")}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {selectedHotelRoomPlans.length ? (
                    selectedHotelRoomPlans.map((roomPlan) => (
                      <tr key={`${roomPlan.roomId}-${roomPlan.planId}`}>
                        <td style={getStickyColumnStyle(280)}>
                          <div className="fw-bold">{roomPlan.roomName}</div>
                          <div className="text-muted small">{roomPlan.planName}</div>
                        </td>
                        {roomPlan.daily.map((stats) => (
                          <td key={stats.date}>{renderAvailabilityCell(stats)}</td>
                        ))}
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td colSpan={days.length + 1} className="text-center py-4">
                        No approved room plans found for this hotel.
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default Dashboard;